import styled from "@emotion/styled";
import { ChangeEvent } from "react";

type FileInputProps = {
  /**
   * Callback function when a file is selected.
   * @param event - The change event containing the selected file(s).
   */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /**
   * File types to accept (e.g., ".json", ".csv", "image/*").
   * Defaults to ".json".
   */
  accept?: string;
  /**
   * Label text for the file input.
   * Defaults to "Import Data".
   */
  label?: string;
  /**
   * Custom button text.
   * Defaults to "Select File".
   */
  buttonText?: string;
  /**
   * Custom icon (e.g., JSX element or emoji).
   * Defaults to "📄".
   */
  icon?: React.ReactNode;
  /**
   * Custom className for the container.
   */
  className?: string;
};

const FileInputContainer = styled.div<{ className?: string }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  ${({ className }) => className && `& { ${className} }`}
`;

const FileInputLabel = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 1rem;
`;

const StyledFileInput = styled.div`
  position: relative;
  width: 100%;
`;

const FileInputButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #333;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;

  &:hover {
    background-color: #e9e9e9;
    border-color: #d0d0d0;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(51, 153, 255, 0.2);
  }
`;

const FileInputIcon = styled.div`
  color: #666;
  margin-right: 12px;
`;

const FileInputHidden = styled.input`
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  border: none;
`;

export const FileInput = ({
  onChange,
  accept = ".json",
  label = "",
  buttonText = "Select File",
  icon = "📄",
  className = "",
}: FileInputProps) => {
  return (
    <FileInputContainer className={className}>
      <FileInputLabel>{label}</FileInputLabel>
      <StyledFileInput>
        <FileInputHidden
          type="file"
          accept={accept}
          onChange={onChange}
        />
        <FileInputButton>
          <FileInputIcon>{icon}</FileInputIcon>
          {buttonText}
        </FileInputButton>
      </StyledFileInput>
    </FileInputContainer>
  );
};
